import * as React from "react";
import { AlertCircle, ChevronRight, GalleryHorizontalEnd, Info, Megaphone } from "lucide-react";
import { PostFlowProvider, usePostFlowContext } from "@/components/PostDialogs/SharedPostFlowContext";
import { CaptionCoachStep } from "@/components/PostDialogs/PostFlowComonents/CaptionCoachStep";
import { SharedPostFlowDefaults } from "@/components/PostDialogs/SharedPostFlowDefaults";
import { postFlowPlayerQuery } from "@/components/PostDialogs/SharedPostFlowContext/hooks/useSharedPostFlow";
import { Alert, Button, Dialog, Skeleton, Spinner } from "@/components/ui";
import { CoachSettings, PostPlayerRequest } from "@/interfaces";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PostServices } from "@/services";
import { DesignRenderGroupServices } from "@/services/bedrock/DesignRenderGroupServices";
import { designKeys } from "@/services/bedrock/queryKeys";
import { usePlayerSettings, useSocialCoachSession } from "@/utils";
import { usePageThemes } from "@/utils/usePageThemes";
import { BackgroundCell, hasBackgroundChoice } from "./BackgroundCell";
import { LookCell } from "./LookCell";
import { StyleBar } from "./StyleBar";
import { CaptionField } from "./CaptionField";
import { ComposerSkeleton } from "./ComposerSkeleton";
import { CarouselPanel } from "./CarouselPanel";
import { DesignChooser } from "./DesignChooser";
import { LeadReviewPicker } from "./LeadReviewPicker";
import { firstNameOf, PostOptions, postTimingOf } from "./PostOptions";
import { PostPreview } from "./PostPreview";
import { SharePostedView } from "./SharePostedView";
import { CAROUSEL_DEFAULT_REVIEWS, isLibraryEntry, useShareFlow } from "./ShareFlowProvider";
import { hasThemeChoice, ThemeCell } from "./ThemeCell";

function dialogTitle(flow: ReturnType<typeof useShareFlow>, playerName: string | null | undefined): string {
  const entry = flow.entry;
  if (entry?.kind === "coach") {
    const player = firstNameOf(playerName);
    if (entry.source !== "review") return player ? `Post for ${player}` : "Post for a player";
    const reviewer = firstNameOf(flow.pinnedReview?.displayName);
    if (reviewer && player) return `Post ${reviewer}'s Review for ${player}`;
    return reviewer ? `Post ${reviewer}'s Review` : "Post This Review";
  }
  if (entry?.kind === "library") {
    return flow.design?.contextType === "review" ? "Post a Review" : flow.design?.name ?? "Share on social";
  }
  const name = firstNameOf(flow.pinnedReview?.displayName);
  return name ? `Post ${name}'s Review` : "Post This Review";
}

// As tall as the dialog allows: the screen less the dialog's margin, header, footer, padding, and the
// post card's own header and action row. Never shorter than a usable preview.
const MEDIA_MAX_HEIGHT = "max(16rem, calc(100dvh - 310px))";

const SECTION_LABEL = "text-xs font-bold uppercase tracking-wider text-slate-400";

/** The look tiles; from the library the look comes with the prompt, so it's a line in the style row instead. */
const LookSection: React.FC = () => {
  const flow = useShareFlow();
  if (isLibraryEntry(flow.entry)) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="look-section">
      <span className={SECTION_LABEL}>Pick a look</span>
      <DesignChooser
        designs={flow.looks}
        loading={flow.designsLoading}
        selectedId={flow.designPackageId}
        onSelect={flow.selectDesign}
        showMultiImageHint={flow.mode === "single"}
      />
    </div>
  );
};

const CarouselOption: React.FC = () => {
  const flow = useShareFlow();
  const others = flow.postableReviews.filter(r => Number(r.id) !== flow.reviewIds[0]).length;
  // What the carousel will actually hold: this review plus the best others, up to the starting cap.
  const count = Math.min(
    CAROUSEL_DEFAULT_REVIEWS,
    flow.carouselLooks[0]?.maxReviews ?? CAROUSEL_DEFAULT_REVIEWS,
    others + 1
  );
  return (
    <button
      type="button"
      onClick={flow.startCarousel}
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-sm shadow-xs transition-all hover:border-slate-300 hover:bg-slate-50 cursor-pointer group"
    >
      <GalleryHorizontalEnd className="size-4.5 shrink-0 text-slate-500 group-hover:text-primary" />
      <span className="font-bold text-slate-800 group-hover:text-primary">Make it a carousel</span>
      <span className="text-slate-500">· Show {count} reviews in one post</span>
      <ChevronRight className="ml-auto size-4 text-slate-400" />
    </button>
  );
};

/** Caption Coach for designs that post the prompt's caption, as for a static prompt. */
const CaptionCoachDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { pendingCaption, setPendingCaption, setDefaultMessage, setHashtags, blockedWords, resetMessageTouched } =
    usePostFlowContext();
  const apply = () => {
    if (pendingCaption) {
      // The player chose this caption, so it replaces their edits.
      resetMessageTouched();
      const hashtags = pendingCaption.match(/#[\w-]+/g) || [];
      setDefaultMessage(pendingCaption.replace(/\s?#[\w-]+/g, "").trim());
      setHashtags(hashtags.join(" "));
      setPendingCaption(null);
    }
    onClose();
  };
  const cancel = () => {
    setPendingCaption(null);
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={next => !next && cancel()} size="lg" showCloseButton>
      <Dialog.Header>
        <Dialog.Title>Generate Caption</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content className="max-h-[70vh] overflow-y-auto">{open && <CaptionCoachStep />}</Dialog.Content>
      <Dialog.Footer>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={cancel}>
            Back
          </Button>
          <Button onClick={apply} disabled={!pendingCaption || blockedWords.length > 0}>
            Apply Caption
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
};

const SAVE_CAPTION_AFTER_MS = 1000;

/**
 * Keeps the render's caption in step with the caption box (a caption that landed after the render,
 * a rewrite, a new look, the player's edits) a moment after it settles, so a reload or another tab
 * shows the same text. Review designs only; bedrock keeps no caption for the others.
 */
function useSaveCaption(groupId: number | null, savedCaption: string | null, text: string | undefined) {
  const queryClient = useQueryClient();
  const inFlight = React.useRef<string | null>(null);
  React.useEffect(() => {
    const caption = text?.trim();
    if (groupId == null || !caption || caption === savedCaption?.trim()) return undefined;
    const key = `${groupId}:${caption}`;
    if (inFlight.current === key) return undefined;
    const timer = setTimeout(() => {
      inFlight.current = key;
      DesignRenderGroupServices.update(groupId, { caption })
        .then(updated => queryClient.setQueryData(designKeys.renderGroup(updated.id), updated))
        // Only a convenience for reloads; the post itself sends the caption box's text.
        .catch(() => undefined)
        .finally(() => {
          if (inFlight.current === key) inFlight.current = null;
        });
    }, SAVE_CAPTION_AFTER_MS);
    return () => clearTimeout(timer);
  }, [groupId, savedCaption, text]);
}

const ComposerBody: React.FC = () => {
  const flow = useShareFlow();
  const { session } = useSocialCoachSession();
  const { themes, isLoading: themesLoading } = usePageThemes();
  const { hasSetting } = usePlayerSettings(flow.playerId ?? undefined);
  const {
    message,
    hashtags,
    setHashtags,
    buildPostRequest,
    validatePostRequest,
    deliveryOptions,
    isDeliveryStepIncomplete,
    blockedWords,
    isPosting,
    setIsPosting,
    postError,
    setPostError,
    onBehalfOf,
    effectivePlayer,
  } = usePostFlowContext();
  const [coachOpen, setCoachOpen] = React.useState(false);

  const { design, render } = flow;
  const group = render.group;
  const isReview = design?.contextType === "review";
  const libraryEntry = isLibraryEntry(flow.entry);
  const playerFirstName = firstNameOf(effectivePlayer?.fullName) ?? "the player";

  // Each design carries its prompt's hashtags.
  React.useEffect(() => {
    const next = design?.promptSettings?.hashtags ?? "";
    if (design && hashtags !== next) setHashtags(next);
  }, [design?.id]);

  useSaveCaption(isReview && group?.status === "success" ? group.id : null, group?.caption ?? null, message);

  if (!design) return null;

  const inFlight = render.isInFlight || flow.renderStarting || (flow.renderGroupId != null && !group);
  const failed = render.isFailed || (flow.renderStartFailed && !flow.renderStarting);
  const ready = group?.status === "success";
  const busy = inFlight && !failed;

  // A prompt from the library brings its look, so the look is a setting in the bar rather than a grid.
  const showLook = libraryEntry && isReview;
  const showTheme = hasThemeChoice(themes, themesLoading);
  const showBackground = hasBackgroundChoice(design.pickerBackgrounds, design.backgroundPolicy);

  // Review captions are written for the post, so they're always editable; others follow the coach's rule,
  // which doesn't bind the coach themselves.
  const allowCaptionEditing =
    onBehalfOf || isReview || !design.promptId || hasSetting(CoachSettings.POST_CUSTOM_MESSAGE, true);
  const canUseCaptionCoach = !isReview && allowCaptionEditing && hasSetting(CoachSettings.CAPTION_COACH, true);

  const timing = postTimingOf(deliveryOptions.isAutoPost, deliveryOptions.sendMode);
  const actionLabel = onBehalfOf
    ? timing === "later"
      ? `Schedule for ${playerFirstName}`
      : timing === "phone"
      ? `Send to ${playerFirstName}`
      : `Post for ${playerFirstName}`
    : timing === "later"
    ? "Schedule post"
    : timing === "phone"
    ? "Send to my phone"
    : "Post now";
  let blockedReason: string | null = null;
  const reviewsNeeded = isReview ? Math.max(1, design.minReviews) : 0;
  if (flow.reviewIds.length < reviewsNeeded)
    blockedReason =
      reviewsNeeded === 1
        ? "You don't have a review ready to share yet."
        : `This design needs ${reviewsNeeded} reviews ready to share.`;
  else if (!ready) blockedReason = "Your post is still building.";
  else if (blockedWords.length > 0) blockedReason = "Edit the caption to remove the flagged words.";
  else if (deliveryOptions.isAutoPost && deliveryOptions.socialNetworks.length === 0)
    blockedReason = "Pick at least one place to post.";
  else if (isDeliveryStepIncomplete) blockedReason = "Pick a date and time.";

  const post = async () => {
    if (!group) return;
    try {
      setIsPosting(true);
      setPostError(undefined);
      const base = buildPostRequest();
      if (!validatePostRequest(base)) return;
      // ms-coaching attaches the render group's own media group, so none is sent.
      const request: PostPlayerRequest = {
        ...base,
        mediaGroup: undefined,
        playerId: flow.playerId ?? undefined,
        promptId: design.promptId ?? undefined,
        designRenderGroupId: group.id,
        reviewIds: group.reviewIds,
      };
      flow.onPosted(await PostServices.createAsPlayer(request));
    } catch (e: any) {
      setPostError(e?.message || "We couldn't post this. Try again.");
    } finally {
      setIsPosting(false);
    }
  };

  if (flow.posted) {
    return (
      <Dialog.Content className="min-h-0 overflow-y-auto">
        <SharePostedView postId={flow.postedPostId} onViewPost={postId => flow.openPost(postId)} onDone={flow.close} />
      </Dialog.Content>
    );
  }

  return (
    <>
      {/* Only this part scrolls; the footer with Post always stays on screen. */}
      <Dialog.Content className="min-h-0 overflow-y-auto">
        <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-12">
          {/* Stays in view while a long carousel list scrolls beside it. */}
          <div className="flex min-w-0 flex-col gap-2 md:sticky md:top-0 md:col-span-5 md:self-start">
            <PostPreview
              group={group}
              aspectRatio={design.aspectRatio}
              isInFlight={inFlight || (!group && !failed)}
              isFailed={failed}
              onRetry={flow.retryRender}
              authorName={(onBehalfOf ? effectivePlayer?.fullName : session?.fullName) ?? ""}
              authorPhotoUrl={onBehalfOf ? effectivePlayer?.photoUrl : session?.photoUrl}
              maxMediaHeight={MEDIA_MAX_HEIGHT}
            />
          </div>

          <div className="flex min-w-0 flex-col gap-4 md:col-span-7">
            {onBehalfOf && !design.publishedAt && (
              <Alert data-testid="unpublished-notice">
                <Info />
                <Alert.Description>
                  This design isn't published yet. Only this post will use it. Players won't see it in their library
                  until you publish.
                </Alert.Description>
              </Alert>
            )}
            {libraryEntry && isReview && <LeadReviewPicker />}
            <LookSection />
            {(showLook || showTheme || showBackground) && (
              <StyleBar>
                {showLook && <LookCell disabled={busy} />}
                {showTheme && (
                  <ThemeCell
                    themeId={group?.contentPageThemeId ?? flow.themeId}
                    themes={themes}
                    defaultThemeId={flow.defaultThemeId}
                    loading={themesLoading}
                    disabled={busy}
                    onSelect={flow.selectTheme}
                  />
                )}
                {showBackground && (
                  <BackgroundCell
                    backgrounds={design.pickerBackgrounds}
                    policy={design.backgroundPolicy}
                    currentAssetId={group ? group.backgroundAssetId : flow.backgroundAssetId}
                    currentUrl={group ? group.backgroundImageUrl : flow.backgroundImageUrl}
                    isCarousel={design.isCarousel || flow.mode === "carousel"}
                    disabled={busy}
                    onSelect={flow.selectBackground}
                    playerId={flow.playerId}
                    ratio={design.aspectRatio}
                    contextType={design.contextType}
                  />
                )}
              </StyleBar>
            )}
            {isReview && flow.mode === "single" && flow.canCarousel && <CarouselOption />}
            {flow.mode === "carousel" && <CarouselPanel />}
            <CaptionField
              caption={isReview ? flow.caption : flow.designCaption ?? undefined}
              onOpenCaptionCoach={canUseCaptionCoach ? () => setCoachOpen(true) : undefined}
              disabled={!allowCaptionEditing || isPosting}
            />
            {postError && (
              <Alert variant="destructive">
                <AlertCircle />
                <Alert.Title>We couldn't post this</Alert.Title>
                <Alert.Description>{postError}</Alert.Description>
              </Alert>
            )}
          </div>
        </div>
        {canUseCaptionCoach && <CaptionCoachDialog open={coachOpen} onClose={() => setCoachOpen(false)} />}
      </Dialog.Content>

      <Dialog.Footer className="flex-col sm:flex-row sm:items-center sm:justify-between">
        <PostOptions />
        <div className="flex flex-col items-stretch gap-1.5 sm:flex-row sm:items-center sm:gap-3">
          {/* Shown, not hovered: it has to work on phones too. */}
          {blockedReason && (
            <p className="text-center text-xs text-slate-500 sm:text-right" data-testid="post-blocked-reason">
              {blockedReason}
            </p>
          )}
          <Button className="font-bold sm:min-w-36" disabled={!!blockedReason || isPosting} onClick={post}>
            {isPosting ? (
              <>
                <Spinner className="mr-2 size-4" />
                Posting…
              </>
            ) : (
              actionLabel
            )}
          </Button>
        </div>
      </Dialog.Footer>
    </>
  );
};

/** One screen: the player's own post, the look, the caption, and Post. */
export const ShareComposer: React.FC = () => {
  const flow = useShareFlow();
  const { design, caption, designCaption } = flow;
  const isOpen = flow.phase === "open";

  // A fresh post flow per opened share; it outlives look and review changes so caption edits survive them.
  const [session, setSession] = React.useState(0);
  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    if (isOpen && !wasOpen.current) setSession(n => n + 1);
    wasOpen.current = isOpen;
  }, [isOpen]);

  const isReview = design?.contextType === "review";
  const onBehalfOf = flow.entry?.kind === "coach";
  const settings = design?.promptSettings ?? null;
  const { data: player } = useQuery({
    ...postFlowPlayerQuery(flow.playerId ?? undefined),
    enabled: onBehalfOf && flow.playerId != null,
  });
  const defaults: SharedPostFlowDefaults = {
    promptId: design?.promptId ?? undefined,
    message: isReview
      ? caption.status === "ready"
        ? caption.caption ?? undefined
        : undefined
      : designCaption
      ? designCaption.status === "ready"
        ? designCaption.caption ?? undefined
        : undefined
      : settings?.message ?? undefined,
    hashtags: settings?.hashtags ?? undefined,
    addDefaultHashtags: settings?.allowHashtags,
    addShortLink: settings?.allowShortLink,
    addDisclaimer: settings?.allowComplianceDisclaimer,
    playerId: flow.playerId ?? undefined,
    playerUserId: flow.playerUserId ?? undefined,
    onBehalfOf,
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={next => !next && flow.close()}
      size="2xl"
      showCloseButton
      className="max-h-[calc(100dvh-2rem)] grid-rows-[auto_minmax(0,1fr)_auto]"
    >
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2 font-bold text-slate-800 select-none">
          <Megaphone className="size-5 shrink-0 text-indigo-600" />
          {dialogTitle(flow, player?.fullName)}
        </Dialog.Title>
      </Dialog.Header>
      {flow.resolving || !design ? (
        <ComposerSkeleton showLooks={!isLibraryEntry(flow.entry)} />
      ) : (
        <PostFlowProvider key={session} defaults={defaults}>
          <ComposerBody />
        </PostFlowProvider>
      )}
    </Dialog>
  );
};
